/**
 * Grid charts (D529): `HeatmapGrid` (rows × columns, e.g. hour × weekday), `CalendarHeatmap` (days as weeks × weekdays) and
 * `CohortGrid` (the retention triangle). All three paint the five-step heat scale of `@vello/tokens` and ALSO print the value
 * in a cell that is big enough, so the colour is never the only signal; a cell without data is an empty dashed box (and a
 * cohort period not yet observed says so in its tooltip), never a zero.
 */
import { useRef, type ReactElement } from 'react';
import {
  buildCalendarGrid,
  buildGridSummary,
  buildHeatGrid,
  clamp,
  cohortTriangle,
  heatLevel,
  isFiniteNumber,
  roundCoord,
  type CohortInput,
  type HeatGridOptions,
} from '@vello/charts-core';
import {
  ChartToolbar,
  HeatLegend,
  chartState,
  chosenAttr,
  emitSelect,
  formatPercentDefault,
  heatFill,
  heatInk,
  pointerPosition,
  truncate,
  useActiveCell,
  AXIS_CHAR_WIDTH,
  type CellRef,
  type ChartSelectProps,
} from './bi-kit';
import type { ChartCsv, ChartExportProps } from './export';
import { activeAttr, markAttrs } from './mark-state';
import { ChartLive, ChartTooltip, cascade, cx, defaultFormatValue, useElementWidth, useMotionState } from './kit';
import type { ChartBaseProps } from './line-chart';

const FALLBACK_WIDTH = 480;
const COLUMN_HEADER = 22;
const GAP = 2;
const RIGHT = 4;

/** The words a grid summary needs (injected). */
export interface GridSummaryWords {
  /** Introduces the strongest cells, e.g. "Highest". */
  readonly highest?: string;
  readonly lowest?: string;
  /** Printed when no cell has data. */
  readonly empty?: string;
}

export interface HeatmapGridProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  /** One label per row (e.g. the weekdays), already localized. */
  readonly rowLabels: readonly string[];
  /** One label per column (e.g. the hours). */
  readonly colLabels: readonly string[];
  /** `values[row][col]`; `null` is "no data". */
  readonly values: readonly (readonly (number | null | undefined)[])[];
  /** The keys sent to `onSelect` for a row / column (default: the labels). */
  readonly rowKeys?: readonly string[];
  readonly colKeys?: readonly string[];
  /** The dimension ids of the rows and columns (selection reports each in turn: columns, then rows). */
  readonly rowDimension?: string;
  readonly colDimension?: string;
  /** Height of one row in SVG units (default 28). */
  readonly cellHeight?: number;
  /** Heat levels, domain and scale (see `buildHeatGrid`). */
  readonly domain?: HeatGridOptions['domain'];
  readonly scale?: HeatGridOptions['scale'];
  /** Print the value in every cell that fits (default true). */
  readonly showValues?: boolean;
  /** A compact text for the value inside a cell (default: `formatValue`). */
  readonly formatCell?: (value: number) => string;
  /** Show the low → high ramp (default true). */
  readonly showLegend?: boolean;
  readonly summaryWords?: GridSummaryWords;
}

/** A rows × columns heat grid with value cells, keyboard navigation over the cells and a tooltip per cell. */
export function HeatmapGrid(props: HeatmapGridProps): ReactElement {
  const height = COLUMN_HEADER + Math.max(1, props.rowLabels.length) * (props.cellHeight ?? 28);
  const hasData = props.rowLabels.length > 0 && props.colLabels.length > 0 && props.values.some((r) => r.some(isFiniteNumber));
  return chartState(props, height, hasData) ?? <HeatmapGridPlot {...props} />;
}

function HeatmapGridPlot(props: HeatmapGridProps): ReactElement {
  const { rowLabels, colLabels } = props;
  const rows = rowLabels.length;
  const cols = colLabels.length;
  const format = props.formatValue ?? defaultFormatValue;
  const formatCell = props.formatCell ?? format;
  const cellH = props.cellHeight ?? 28;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const rowKey = (r: number): string => props.rowKeys?.[r] ?? rowLabels[r] ?? '';
  const colKey = (c: number): string => props.colKeys?.[c] ?? colLabels[c] ?? '';
  const activate = props.onSelect !== undefined ? (cell: CellRef): void => {
    emitSelect(props, colKey(cell.col), props.colDimension);
    emitSelect(props, rowKey(cell.row), props.rowDimension);
  } : undefined;
  const active = useActiveCell(rows, cols, activate);

  const grid = buildHeatGrid({
    rows,
    cols,
    cells: props.values.flatMap((r, row) => r.slice(0, cols).map((value, col) => ({ row, col, value }))),
    ...(props.domain !== undefined ? { domain: props.domain } : {}),
    ...(props.scale !== undefined ? { scale: props.scale } : {}),
  });
  const gutter = clamp(rowLabels.reduce((m, l) => Math.max(m, l.length), 0) * AXIS_CHAR_WIDTH + 12, 28, 120);
  const cellW = Math.max(4, (width - gutter - RIGHT) / cols);
  const height = COLUMN_HEADER + rows * cellH;
  const strideX = Math.max(1, Math.ceil(34 / cellW));
  const fitsValue = (props.showValues ?? true) && cellW >= 30 && cellH >= 18;
  const cellAt = (x: number, y: number): CellRef | null => {
    const col = Math.floor((x - gutter) / cellW);
    const row = Math.floor((y - COLUMN_HEADER) / cellH);
    return row >= 0 && row < rows && col >= 0 && col < cols ? { row, col } : null;
  };

  const summary =
    props.ariaLabel ??
    buildGridSummary({
      ...(props.title !== undefined ? { title: props.title } : {}),
      rowLabels,
      colLabels,
      values: grid.cells.map((c) => c.value),
      format,
      ...(props.summaryWords?.highest !== undefined ? { highestLabel: props.summaryWords.highest } : {}),
      ...(props.summaryWords?.lowest !== undefined ? { lowestLabel: props.summaryWords.lowest } : {}),
      ...(props.summaryWords?.empty !== undefined ? { emptyText: props.summaryWords.empty } : {}),
    });
  const csv = (): ChartCsv => ({ header: ['', ...colLabels], rows: rowLabels.map((l, r) => [l, ...colLabels.map((_, c) => grid.cells[r * cols + c]?.value ?? null)]) });
  const cur = active.cell;
  const curValue = cur === null ? null : (grid.cells[cur.row * cols + cur.col]?.value ?? null);
  const curText = cur === null ? '' : `${rowLabels[cur.row] ?? ''} ${colLabels[cur.col] ?? ''}: ${curValue === null ? '–' : format(curValue)}`;

  return (
    <div className={cx('vello-chart', 'vello-chart--heatmap', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div
        ref={ref}
        className="vello-chart__plot vello-chart__focus"
        role="img"
        aria-label={summary}
        tabIndex={0}
        onKeyDown={active.onKeyDown}
        onFocus={active.onFocus}
        onBlur={active.onBlur}
      >
        <svg
          className="vello-chart__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          onPointerMove={(e) => {
            active.markPointer();
            const p = pointerPosition(e);
            active.set(cellAt(p.x, p.y));
          }}
          onPointerLeave={active.onPointerLeave}
          onClick={(e) => {
            const p = pointerPosition(e);
            const c = cellAt(p.x, p.y);
            if (c !== null && activate !== undefined) activate(c);
          }}
        >
          {colLabels.map((label, c) =>
            c % strideX === 0 ? (
              <text key={`c${c}`} className="vello-chart__tick" x={gutter + (c + 0.5) * cellW} y={COLUMN_HEADER - 8} textAnchor="middle">
                {label}
              </text>
            ) : null,
          )}
          {rowLabels.map((label, r) => (
            <text key={`r${r}`} className="vello-chart__tick" x={gutter - 8} y={COLUMN_HEADER + (r + 0.5) * cellH} dy="0.32em" textAnchor="end">
              {label}
            </text>
          ))}
          {grid.cells.map((cell) => {
            const x = gutter + cell.col * cellW + GAP / 2;
            const y = COLUMN_HEADER + cell.row * cellH + GAP / 2;
            const w = Math.max(1, cellW - GAP);
            const h = Math.max(1, cellH - GAP);
            const empty = cell.value === null;
            const isActive = cur !== null && cur.row === cell.row && cur.col === cell.col;
            const chosen = chosenAttr(props, rowKey(cell.row));
            const chosenCol = chosenAttr(props, colKey(cell.col));
            const dim = chosen['data-chosen'] === 'false' && chosenCol['data-chosen'] === 'false';
            return (
              <g key={`${cell.row}:${cell.col}`} className="vello-chart__cell-in" style={cascade(cell.col)} data-empty={empty ? 'true' : undefined} data-chosen={props.selected !== undefined && props.selected.length > 0 ? (dim ? 'false' : 'true') : undefined} {...activeAttr(isActive)}>
                <rect className="vello-chart__cell" x={roundCoord(x)} y={roundCoord(y)} width={roundCoord(w)} height={roundCoord(h)} rx={3} fill={heatFill(cell.level)} data-empty={empty ? 'true' : undefined} />
                {isActive && <rect className="vello-chart__cell-active" x={roundCoord(x)} y={roundCoord(y)} width={roundCoord(w)} height={roundCoord(h)} rx={3} />}
                {fitsValue && !empty && (
                  <text className="vello-chart__cell-value tnum" x={x + w / 2} y={y + h / 2} dy="0.32em" textAnchor="middle" fill={heatInk(cell.level)}>
                    {formatCell(cell.value as number)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {cur !== null && (
          <ChartTooltip
            x={gutter + (cur.col + 0.5) * cellW}
            y={COLUMN_HEADER + cur.row * cellH}
            width={width}
            heading={`${rowLabels[cur.row] ?? ''} · ${colLabels[cur.col] ?? ''}`}
            rows={[{ key: 'v', value: curValue === null ? '–' : format(curValue) }]}
          />
        )}
      </div>
      <ChartLive text={curText} />
      {(props.showLegend ?? true) && grid.domain !== null && <HeatLegend min={format(grid.domain[0])} max={format(grid.domain[1])} />}
    </div>
  );
}

// ── Calendar ───────────────────────────────────────────────────────────────────────────────────────────────────────

export interface CalendarHeatmapProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  /** Market-local day keys `YYYY-MM-DD` with their values. */
  readonly days: readonly { readonly day: string; readonly value: number | null | undefined }[];
  /** First weekday of the week, 0 = Sunday … 6 = Saturday: from the MARKET (default 0). */
  readonly weekStart?: number;
  /** Seven labels indexed by weekday number (0 = Sunday … 6 = Saturday), e.g. from `Intl.DateTimeFormat`. */
  readonly weekdayLabels: readonly string[];
  /** Twelve labels indexed by month - 1. */
  readonly monthLabels: readonly string[];
  /** The tooltip heading of a day (default: the day key). */
  readonly formatDay?: (dayKey: string) => string;
  readonly from?: string;
  readonly to?: string;
  readonly domain?: HeatGridOptions['domain'];
  readonly scale?: HeatGridOptions['scale'];
  readonly showLegend?: boolean;
  readonly summaryWords?: GridSummaryWords;
}

/** Days as week columns × weekday rows (a year or a quarter at a glance). `onSelect` reports the day key. */
export function CalendarHeatmap(props: CalendarHeatmapProps): ReactElement {
  const hasData = props.days.some((d) => isFiniteNumber(d.value));
  return chartState(props, 7 * 16 + 24, hasData) ?? <CalendarHeatmapPlot {...props} />;
}

function CalendarHeatmapPlot(props: CalendarHeatmapProps): ReactElement {
  const format = props.formatValue ?? defaultFormatValue;
  const formatDay = props.formatDay ?? ((d: string) => d);
  const calendar = buildCalendarGrid({
    days: props.days,
    ...(props.weekStart !== undefined ? { weekStart: props.weekStart } : {}),
    ...(props.from !== undefined ? { from: props.from } : {}),
    ...(props.to !== undefined ? { to: props.to } : {}),
    ...(props.domain !== undefined ? { domain: props.domain } : {}),
    ...(props.scale !== undefined ? { scale: props.scale } : {}),
  });
  const weeks = calendar.weeks.length;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const gutter = clamp(calendar.weekdays.reduce((m, w) => Math.max(m, (props.weekdayLabels[w] ?? '').length), 0) * AXIS_CHAR_WIDTH + 10, 24, 64);
  const size = clamp(Math.floor((width - gutter - RIGHT) / Math.max(1, weeks)), 10, 26);
  const top = 20;
  const height = top + 7 * size;
  const cellOf = (c: CellRef): { day: string; value: number | null; inRange: boolean } => {
    const cell = calendar.weeks[c.col]?.cells[c.row];
    return { day: cell?.day ?? '', value: cell?.value ?? null, inRange: cell?.inRange ?? false };
  };
  const activate = props.onSelect !== undefined ? (c: CellRef): void => { const d = cellOf(c); if (d.inRange) emitSelect(props, d.day, props.dimension ?? 'day'); } : undefined;
  const active = useActiveCell(7, weeks, activate);
  const cellAt = (x: number, y: number): CellRef | null => {
    const col = Math.floor((x - gutter) / size);
    const row = Math.floor((y - top) / size);
    return row >= 0 && row < 7 && col >= 0 && col < weeks ? { row, col } : null;
  };

  const inRange = calendar.weeks.flatMap((w) => w.cells.filter((c) => c.inRange));
  const summary =
    props.ariaLabel ??
    buildGridSummary({
      ...(props.title !== undefined ? { title: props.title } : {}),
      rowLabels: inRange.map((c) => formatDay(c.day)),
      colLabels: [''],
      values: inRange.map((c) => c.value),
      format,
      cellJoin: '',
      ...(props.summaryWords?.highest !== undefined ? { highestLabel: props.summaryWords.highest } : {}),
      ...(props.summaryWords?.lowest !== undefined ? { lowestLabel: props.summaryWords.lowest } : {}),
      ...(props.summaryWords?.empty !== undefined ? { emptyText: props.summaryWords.empty } : {}),
    });
  const csv = (): ChartCsv => ({ header: ['', ''], rows: inRange.map((c) => [c.day, c.value]) });
  const cur = active.cell;
  const curCell = cur === null ? null : cellOf(cur);
  const shown = curCell !== null && curCell.inRange ? curCell : null;

  return (
    <div className={cx('vello-chart', 'vello-chart--calendar', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div
        ref={ref}
        className="vello-chart__plot vello-chart__focus"
        role="img"
        aria-label={summary}
        tabIndex={0}
        onKeyDown={active.onKeyDown}
        onFocus={active.onFocus}
        onBlur={active.onBlur}
      >
        <svg
          className="vello-chart__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          onPointerMove={(e) => {
            active.markPointer();
            const p = pointerPosition(e);
            active.set(cellAt(p.x, p.y));
          }}
          onPointerLeave={active.onPointerLeave}
          onClick={(e) => {
            const p = pointerPosition(e);
            const c = cellAt(p.x, p.y);
            if (c !== null && activate !== undefined) activate(c);
          }}
        >
          {calendar.weekdays.map((w, r) => (
            <text key={`w${w}`} className="vello-chart__tick" x={gutter - 6} y={top + (r + 0.5) * size} dy="0.32em" textAnchor="end">
              {r % 2 === 0 || size >= 16 ? truncate(props.weekdayLabels[w] ?? '', 4) : ''}
            </text>
          ))}
          {calendar.weeks.map((week) =>
            week.monthLabel !== null ? (
              <text key={`m${week.index}`} className="vello-chart__tick" x={gutter + week.index * size} y={top - 8}>
                {props.monthLabels[week.monthLabel - 1] ?? ''}
              </text>
            ) : null,
          )}
          {calendar.weeks.map((week) =>
            week.cells.map((cell) => {
              if (!cell.inRange) return null;
              const x = gutter + week.index * size + 1;
              const y = top + cell.column * size + 1;
              const isActive = cur !== null && cur.row === cell.column && cur.col === week.index;
              return (
                <g key={cell.day} className="vello-chart__cell-in" style={cascade(week.index)} {...markAttrs(props, cell.day, isActive)}>
                  <rect className="vello-chart__cell" x={x} y={y} width={size - 2} height={size - 2} rx={2} fill={heatFill(cell.level)} data-empty={cell.value === null ? 'true' : undefined} />
                  {isActive && <rect className="vello-chart__cell-active" x={x} y={y} width={size - 2} height={size - 2} rx={2} />}
                </g>
              );
            }),
          )}
        </svg>
        {shown !== null && cur !== null && (
          <ChartTooltip x={gutter + (cur.col + 0.5) * size} y={top + cur.row * size} width={width} heading={formatDay(shown.day)} rows={[{ key: 'v', value: shown.value === null ? '–' : format(shown.value) }]} />
        )}
      </div>
      <ChartLive text={shown === null ? '' : `${formatDay(shown.day)}: ${shown.value === null ? '–' : format(shown.value)}`} />
      {(props.showLegend ?? true) && calendar.grid.domain !== null && <HeatLegend min={format(calendar.grid.domain[0])} max={format(calendar.grid.domain[1])} />}
    </div>
  );
}

// ── Cohort retention ───────────────────────────────────────────────────────────────────────────────────────────────

export interface CohortGridProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  readonly cohorts: readonly CohortInput[];
  /** The column header of a period since the start (e.g. `W0`, `W1`; localized by the caller). */
  readonly periodLabel: (period: number) => string;
  /** Header over the cohort sizes (e.g. "Size"). */
  readonly sizeLabel: string;
  /** The name of the weighted-average row (e.g. "Average"). */
  readonly averageLabel: string;
  /** Format a retention rate (a fraction 0 … 1; default a whole percent). */
  readonly formatRate?: (fraction: number) => string;
  /** The word for a period that has not happened yet, added to its tooltip. */
  readonly pendingLabel?: string;
  readonly cellHeight?: number;
  readonly summaryWords?: GridSummaryWords;
}

/** The retention triangle: a row per cohort, a column per period, the weighted average row below. `onSelect` reports the cohort. */
export function CohortGrid(props: CohortGridProps): ReactElement {
  const hasData = props.cohorts.some((c) => c.size > 0 && c.retained.some(isFiniteNumber));
  return chartState(props, COLUMN_HEADER + Math.max(1, props.cohorts.length + 1) * (props.cellHeight ?? 28), hasData) ?? <CohortGridPlot {...props} />;
}

function CohortGridPlot(props: CohortGridProps): ReactElement {
  const triangle = cohortTriangle(props.cohorts);
  const rows = triangle.rows.length;
  const periods = triangle.periods;
  const totalRows = rows + 1; // + the average row
  const formatRate = props.formatRate ?? formatPercentDefault;
  const format = props.formatValue ?? defaultFormatValue;
  const cellH = props.cellHeight ?? 28;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const labelChars = Math.max(props.averageLabel.length, ...triangle.rows.map((r) => r.cohort.length));
  const sizeChars = Math.max(props.sizeLabel.length, ...triangle.rows.map((r) => format(r.size).length));
  const labelW = clamp(labelChars * AXIS_CHAR_WIDTH + 10, 40, 110);
  const sizeW = clamp(sizeChars * AXIS_CHAR_WIDTH + 12, 36, 80);
  const gutter = labelW + sizeW;
  const cellW = Math.max(6, (width - gutter - RIGHT) / Math.max(1, periods));
  const height = COLUMN_HEADER + totalRows * cellH;
  const activate = props.onSelect !== undefined ? (c: CellRef): void => { const r = triangle.rows[c.row]; if (r !== undefined) emitSelect(props, r.cohort, props.dimension ?? 'cohort'); } : undefined;
  const active = useActiveCell(totalRows, periods, activate);
  const cellAt = (x: number, y: number): CellRef | null => {
    const col = Math.floor((x - gutter) / cellW);
    const row = Math.floor((y - COLUMN_HEADER) / cellH);
    return row >= 0 && row < totalRows && col >= 0 && col < periods ? { row, col } : null;
  };
  const stride = Math.max(1, Math.ceil(30 / cellW));
  const fitsValue = cellW >= 30 && cellH >= 18;
  const thresholds = [0.2, 0.4, 0.6, 0.8];

  const summary =
    props.ariaLabel ??
    buildGridSummary({
      ...(props.title !== undefined ? { title: props.title } : {}),
      rowLabels: triangle.rows.map((r) => r.cohort),
      colLabels: Array.from({ length: periods }, (_, p) => props.periodLabel(p)),
      values: triangle.rows.flatMap((r) => r.cells.map((c) => c.rate)),
      format: formatRate,
      ...(props.summaryWords?.highest !== undefined ? { highestLabel: props.summaryWords.highest } : {}),
      ...(props.summaryWords?.lowest !== undefined ? { lowestLabel: props.summaryWords.lowest } : {}),
      ...(props.summaryWords?.empty !== undefined ? { emptyText: props.summaryWords.empty } : {}),
    });
  const csv = (): ChartCsv => ({
    header: ['', props.sizeLabel, ...Array.from({ length: periods }, (_, p) => props.periodLabel(p))],
    rows: [...triangle.rows.map((r) => [r.cohort, r.size, ...r.cells.map((c) => c.rate)]), [props.averageLabel, null, ...triangle.average]],
  });
  const cur = active.cell;
  const curRate = cur === null ? null : cur.row < rows ? (triangle.rows[cur.row]?.cells[cur.col]?.rate ?? null) : (triangle.average[cur.col] ?? null);
  const curPending = cur !== null && cur.row < rows ? (triangle.rows[cur.row]?.cells[cur.col]?.pending ?? false) : false;
  const curHeading = cur === null ? '' : `${cur.row < rows ? (triangle.rows[cur.row]?.cohort ?? '') : props.averageLabel} · ${props.periodLabel(cur.col)}`;
  const curValueText = curRate === null ? (curPending && props.pendingLabel !== undefined ? props.pendingLabel : '–') : formatRate(curRate);

  const cellEls = (row: number, label: string, size: number | null, cells: readonly { rate: number | null; level: number | null; pending: boolean }[], isAverage: boolean): ReactElement => {
    const y = COLUMN_HEADER + row * cellH;
    return (
      <g key={`row${row}`} className="vello-chart__cell-in" style={cascade(row)} {...(isAverage ? {} : chosenAttr(props, label))} data-average={isAverage ? 'true' : undefined}>
        <text className="vello-chart__tick" x={8} y={y + cellH / 2} dy="0.32em" fontWeight={isAverage ? 700 : undefined}>
          {truncate(label, Math.floor((labelW - 8) / AXIS_CHAR_WIDTH))}
        </text>
        {size !== null && (
          <text className="vello-chart__tick tnum" x={gutter - 8} y={y + cellH / 2} dy="0.32em" textAnchor="end">
            {format(size)}
          </text>
        )}
        {cells.map((cell, p) => {
          const x = gutter + p * cellW + GAP / 2;
          const isActive = cur !== null && cur.row === row && cur.col === p;
          const w = Math.max(1, cellW - GAP);
          const h = Math.max(1, cellH - GAP);
          return (
            <g key={p} {...activeAttr(isActive)}>
              <rect className="vello-chart__cell" x={roundCoord(x)} y={roundCoord(y + GAP / 2)} width={roundCoord(w)} height={roundCoord(h)} rx={3} fill={heatFill(cell.level)} data-empty={cell.rate === null ? 'true' : undefined} data-pending={cell.pending ? 'true' : undefined} />
              {isActive && <rect className="vello-chart__cell-active" x={roundCoord(x)} y={roundCoord(y + GAP / 2)} width={roundCoord(w)} height={roundCoord(h)} rx={3} />}
              {fitsValue && cell.rate !== null && (
                <text className="vello-chart__cell-value tnum" x={x + w / 2} y={y + cellH / 2} dy="0.32em" textAnchor="middle" fill={heatInk(cell.level)}>
                  {formatRate(cell.rate)}
                </text>
              )}
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <div className={cx('vello-chart', 'vello-chart--cohort', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div
        ref={ref}
        className="vello-chart__plot vello-chart__focus"
        role="img"
        aria-label={summary}
        tabIndex={0}
        onKeyDown={active.onKeyDown}
        onFocus={active.onFocus}
        onBlur={active.onBlur}
      >
        <svg
          className="vello-chart__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          onPointerMove={(e) => {
            active.markPointer();
            const p = pointerPosition(e);
            active.set(cellAt(p.x, p.y));
          }}
          onPointerLeave={active.onPointerLeave}
          onClick={(e) => {
            const p = pointerPosition(e);
            const c = cellAt(p.x, p.y);
            if (c !== null && activate !== undefined) activate(c);
          }}
        >
          <text className="vello-chart__tick" x={gutter - 8} y={COLUMN_HEADER - 8} textAnchor="end">
            {truncate(props.sizeLabel, Math.floor((sizeW - 8) / AXIS_CHAR_WIDTH))}
          </text>
          {Array.from({ length: periods }, (_, p) =>
            p % stride === 0 ? (
              <text key={`p${p}`} className="vello-chart__tick" x={gutter + (p + 0.5) * cellW} y={COLUMN_HEADER - 8} textAnchor="middle">
                {props.periodLabel(p)}
              </text>
            ) : null,
          )}
          {triangle.rows.map((r, i) => cellEls(i, r.cohort, r.size, r.cells, false))}
          <line className="vello-chart__baseline" x1={8} x2={width - RIGHT} y1={COLUMN_HEADER + rows * cellH} y2={COLUMN_HEADER + rows * cellH} />
          {cellEls(
            rows,
            props.averageLabel,
            null,
            triangle.average.map((rate) => ({ rate, level: rate === null ? null : heatLevel(rate, thresholds), pending: false })),
            true,
          )}
        </svg>
        {cur !== null && <ChartTooltip x={gutter + (cur.col + 0.5) * cellW} y={COLUMN_HEADER + cur.row * cellH} width={width} heading={curHeading} rows={[{ key: 'v', value: curValueText }]} />}
      </div>
      <ChartLive text={cur === null ? '' : `${curHeading}: ${curValueText}`} />
      <HeatLegend min={formatRate(0)} max={formatRate(1)} />
    </div>
  );
}
